import { useQueries } from '@tanstack/react-query';
import { useEffect, useReducer } from 'react';
import { ENTITY_TYPES, EVENT_STATUSES, IEvent, IEventObject } from 'src/api/getEvents';
import { getPublishPlan } from 'src/api/getPublishPlan/getPublishPlan';
import { ConnectorPlan } from '@weareopenr/types';
import { QueryKeys } from '../QueryKeys';

/** The event object a connector attaches its plan as (connector-oracle `notify-plan-ready`) */
export const PLAN_OBJECT_NAME = 'plan.json';

const PLAN_RETRY_COUNT = 2;

interface IPlanEvent {
  event: IEvent;
  planObject: IEventObject;
}

// `changed` is a publish that was accepted but refused by the connector, as the external system had
// moved on since the plan was made
export type TReviewOutcome = 'accepted' | 'rejected' | 'changed';

export interface IReviewDecision {
  outcome: TReviewOutcome;
  decidedAt: string;
  /** The channels the outcome applies to, as a commit can land differently per channel */
  eventIds: string[];
}

/**
 * The connector attaches its plan to the channel's EXTERNAL_SYSTEM child only. A pricing publish
 * also parks each venue under it as PENDING_REVIEW, but those carry a count, not a plan.
 */
export const getPlanEvents = (event?: IEvent): IPlanEvent[] =>
  (event?.child_events ?? []).flatMap((child) => {
    const planObject = child.objects?.find(({ name }) => name === PLAN_OBJECT_NAME);
    return child.entity_type === ENTITY_TYPES.EXTERNAL_SYSTEM && planObject ? [{ event: child, planObject }] : [];
  });

const isAwaitingReview = ({ event }: IPlanEvent): boolean => event.status === EVENT_STATUSES.PENDING_REVIEW;

const getReviewOutcome = ({ status }: IEvent): TReviewOutcome => {
  if (status === EVENT_STATUSES.REJECTED) {
    return 'rejected';
  } else if (status === EVENT_STATUSES.CAUTION) {
    return 'changed';
  } else {
    return 'accepted';
  }
};

const byLatest = (a: IEvent, b: IEvent): number => b.updated_at.localeCompare(a.updated_at);

// A refused channel is the one the user has to act on, so it wins over the rest of the publish
const getDecision = (events: IEvent[]): IReviewDecision | null => {
  const [latest] = [...events].sort(byLatest);
  if (!latest) return null;

  const outcome = events.some(({ status }) => status === EVENT_STATUSES.CAUTION) ? 'changed' : getReviewOutcome(latest);
  const decided = events.filter((event) => getReviewOutcome(event) === outcome).sort(byLatest);

  return { outcome, decidedAt: decided[0].updated_at, eventIds: decided.map(({ event_id }) => event_id) };
};

// Re-renders when the time passes, so a review left open expires without a refresh
const useHasPassed = (time: number | null): boolean => {
  const [, rerender] = useReducer((count: number) => count + 1, 0);

  useEffect(() => {
    if (time === null) return undefined;
    const timeout = setTimeout(rerender, Math.max(time - Date.now(), 0));
    return () => clearTimeout(timeout);
  }, [time]);

  return time !== null && Date.now() >= time;
};

const getEarliestExpiry = (plans: ConnectorPlan[]): number | null =>
  plans.length > 0 ? Math.min(...plans.map(({ expires_at }) => Date.parse(expires_at))) : null;

export const usePublishPlans = (event?: IEvent) => {
  const planEvents = getPlanEvents(event);

  const results = useQueries({
    queries: planEvents.map((planEvent) => ({
      queryKey: [QueryKeys.publishPlan, planEvent.event.event_id],
      queryFn: () => getPublishPlan(planEvent.planObject),
      // Only fetched whilst awaiting review, but a plan already loaded stays on screen when someone
      // else decides it, so the review can say so rather than vanish
      enabled: isAwaitingReview(planEvent),
      // A plan never changes once produced, and its URL is only signed for an hour.
      staleTime: Infinity,
      // Retried here as the global `retry: false` would leave a one-off failure stuck on the load
      // error, with nothing on the page to refetch it
      retry: PLAN_RETRY_COUNT,
    })),
  });

  const reviewableResults = results.filter((_, index) => isAwaitingReview(planEvents[index]));
  const reviewableEvents = planEvents.filter(isAwaitingReview);
  const hasPendingReview = reviewableEvents.length > 0;
  // A channel can still be awaiting review after another was decided, so a decided plan only shows
  // once nothing is left to review
  const plans = (hasPendingReview ? reviewableResults : results).flatMap(({ data }) =>
    data ? [data] : [],
  ) as ConnectorPlan[];
  const isExpired = useHasPassed(getEarliestExpiry(plans));
  const decidedEvents = hasPendingReview
    ? []
    : planEvents.filter((_, index) => results[index].data).map(({ event: decidedEvent }) => decidedEvent);

  return {
    plans,
    isLoading: reviewableResults.some(({ isLoading }) => isLoading),
    isError: reviewableResults.some(({ isError }) => isError),
    isExpired,
    decision: getDecision(decidedEvents),
    reviewableEventIds: reviewableEvents.map(({ event: { event_id } }) => event_id),
    hasPendingReview,
  };
};
